import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Trash2, Check, Sparkles, X, Info, ShoppingCart } from 'lucide-react';

const SchedulerApp = () => {
  const [tasks, setTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [newTaskDate, setNewTaskDate] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // 画面サイズを監視
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ページ読み込み時にlocalStorageからデータを復元
  useEffect(() => {
    const savedTasks = localStorage.getItem('scheduler-tasks');
    const hasVisited = localStorage.getItem('scheduler-visited');
    
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }
    
    if (!hasVisited) {
      setShowModal(true);
      localStorage.setItem('scheduler-visited', 'true');
    }
  }, []);

  // tasksが変更されるたびにlocalStorageに保存
  useEffect(() => {
    localStorage.setItem('scheduler-tasks', JSON.stringify(tasks));
  }, [tasks]);

  const addTask = () => {
    if (newTask.trim()) {
      const task = {
        id: Date.now().toString(),
        text: newTask,
        date: newTaskDate,
        completed: false,
        createdAt: new Date()
      };
      setTasks([...tasks, task]);
      setNewTask('');
      setNewTaskDate('');
    }
  };

  const toggleTask = (id) => {
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const clearCompleted = () => {
    setTasks(tasks.filter(task => !task.completed));
  };

  // 日付フォーマット
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return '今日';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return '明日';
    } else {
      return date.toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // 期限が過ぎているかチェック
  const isOverdue = (dateString) => {
    if (!dateString) return false;
    const taskDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return taskDate < today;
  };

  // 背景画像を画面サイズに応じて選択
  const getBackgroundImage = () => {
    if (isMobile) {
      return "url('/images/images-mobile.webp')";
    }
    return "url('/images/images.webp')";
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* レスポンシブ対応の美しい背景画像 */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: getBackgroundImage(),
          backgroundAttachment: isMobile ? 'scroll' : 'fixed' // モバイルでは fixed を避ける
        }}
      >
        {/* 美しいオーバーレイ - 上から下へのグラデーション */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 via-slate-50/25 via-teal-100/15 to-transparent"></div>
      </div>
      
      {/* 装飾的な背景要素 */}
      <div className="fixed top-10 left-10 w-32 h-32 bg-gradient-to-r from-teal-200/20 to-slate-200/20 rounded-full blur-3xl" aria-hidden="true"></div>
      <div className="fixed bottom-20 right-20 w-40 h-40 bg-gradient-to-r from-blue-200/20 to-teal-200/20 rounded-full blur-3xl" aria-hidden="true"></div>
      <div className="fixed top-1/2 left-1/4 w-20 h-20 bg-gradient-to-r from-slate-300/15 to-teal-300/15 rounded-full blur-2xl" aria-hidden="true"></div>

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-2xl">
        {/* ヘッダー */}
        <header className="text-center mb-4">
          <div className="inline-flex flex-col items-center gap-4 mb-4">
            <div className="p-3 bg-gradient-to-r from-gray-800 to-black rounded-2xl shadow-lg" aria-hidden="true">
              <ShoppingCart className="w-8 h-8 text-white" />
            </div>
            <div className="text-center">
              <h1 className="text-3xl bg-gradient-to-r from-gray-800 to-black bg-clip-text text-transparent">
                お買い物メモ
              </h1>
              <p className="text-slate-600 text-xl mt-1">買い物リストを管理しましょう</p>
            </div>
          </div>
        </header>

        {/* 新しいタスクを追加 */}
        <section className="backdrop-blur-md border-0 shadow-xl rounded-2xl p-6 mb-8" style={{backgroundColor: 'rgba(255, 255, 255, 0.6)'}}>
          <h2 className="text-xl text-slate-700 mb-4">新しいタスクを追加</h2>
          <form onSubmit={(e) => { e.preventDefault(); addTask(); }} className="space-y-4">
            <label htmlFor="task-input" className="sr-only">タスク名</label>
            <input
              id="task-input"
              type="text"
              placeholder="やることを入力してください..."
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              className="w-full border border-slate-200 focus:border-gray-700 focus:ring-2 focus:ring-gray-200 focus:outline-none rounded-xl bg-white/85 h-12 px-4 py-3"
              maxLength={50}
              required
            />
            <div className="flex gap-3">
              <label htmlFor="task-date" className="sr-only">期日</label>
              <input
                id="task-date"
                type="date"
                value={newTaskDate}
                onChange={(e) => setNewTaskDate(e.target.value)}
                className="flex-1 border border-slate-200 focus:border-gray-700 focus:ring-2 focus:ring-gray-200 focus:outline-none rounded-xl bg-white/85 h-12 px-4 py-3"
              />
              <button
                type="submit"
                className="bg-gradient-to-r from-gray-800 to-black hover:from-gray-900 hover:to-gray-800 text-white border-0 rounded-xl shadow-lg px-8 h-12 flex items-center gap-2 transition-all duration-200 hover:scale-105"
                aria-label="タスクを追加"
              >
                <Plus className="w-4 h-4" aria-hidden="true" />
                追加
              </button>
            </div>
          </form>
        </section>

        {/* タスク一覧 */}
        <section className="backdrop-blur-md border-0 shadow-xl rounded-2xl p-6" style={{backgroundColor: 'rgba(255, 255, 255, 0.6)'}}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl text-slate-700">タスク一覧</h2>
            {tasks.some(task => task.completed) && (
              <button
                onClick={clearCompleted}
                className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-4 py-2 text-sm flex items-center gap-1 transition-all duration-200 hover:scale-105"
                aria-label="完了済みタスクを削除"
              >
                <Sparkles className="w-4 h-4" aria-hidden="true" />
                完了済みを削除
              </button>
            )}
          </div>

          {tasks.length === 0 ? (
            <div className="text-center py-12" role="status" aria-live="polite">
              <div className="w-16 h-16 bg-gradient-to-r from-slate-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                <Calendar className="w-8 h-8 text-slate-500" />
              </div>
              <p className="text-slate-500">まだタスクがありません</p>
            </div>
          ) : (
            <ul className="space-y-3" role="list">
              {tasks.map((task) => (
                <li
                  key={task.id}
                  className={`p-4 rounded-xl border transition-all duration-300 ${
                    task.completed
                      ? 'bg-slate-50/60 border-slate-200'
                      : isOverdue(task.date)
                      ? 'border-red-200'
                      : 'border-slate-100 hover:border-gray-400 hover:shadow-md'
                  }`}
                  style={{
                    backgroundColor: task.completed 
                      ? 'rgba(248, 250, 252, 0.6)' 
                      : isOverdue(task.date)
                      ? 'rgba(254, 242, 242, 1)' 
                      : 'rgba(255, 255, 255, 1)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => toggleTask(task.id)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                          task.completed
                            ? 'bg-gradient-to-r from-gray-800 to-black border-gray-800'
                            : 'border-slate-300 hover:border-gray-600'
                        }`}
                        aria-label={task.completed ? 'タスクを未完了にする' : 'タスクを完了にする'}
                      >
                        {task.completed && (
                          <Check className="w-3 h-3 text-white" aria-hidden="true" />
                        )}
                      </button>
                      <div className="flex-1">
                        <p className={`transition-all duration-200 ${
                          task.completed
                            ? 'line-through text-slate-500'
                            : isOverdue(task.date) && !task.completed
                            ? 'text-red-700 font-semibold'
                            : 'text-slate-700'
                        }`}>
                          {task.text}
                        </p>
                        {task.date && (
                          <p className={`text-xs mt-1 ${
                            task.completed
                              ? 'text-slate-400'
                              : isOverdue(task.date)
                              ? 'text-red-600 font-semibold'
                              : 'text-slate-500'
                          }`}>
                            期日: {formatDate(task.date)}
                            {isOverdue(task.date) && !task.completed && ' (期限切れ)'}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all duration-200 hover:scale-110"
                      aria-label={`${task.text}を削除`}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* フッター */}
        <footer className="text-center mt-8">
          <p className="text-sm backdrop-blur-sm rounded-full px-4 py-2 inline-block" style={{color: 'white'}}>
            効率的で美しい毎日を ✨
          </p>
          <p className="text-white text-xs mt-4 opacity-80">
            © 2025 Aki Y & Claude AI
          </p>
        </footer>
      </main>

      {/* 使い方説明ボタン */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-gray-800 to-black text-white p-3 rounded-full shadow-lg hover:from-gray-900 hover:to-gray-800 transition-all duration-200 hover:scale-110"
        aria-label="使い方を表示"
      >
        <Info size={24} />
      </button>

      {/* 初回訪問者向けモーダル */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
          <div className="backdrop-blur-md rounded-2xl max-w-md w-full p-6 relative shadow-2xl" style={{backgroundColor: 'rgba(255, 255, 255, 0.9)'}}>
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-slate-700 transition-colors"
              aria-label="モーダルを閉じる"
            >
              <X size={24} />
            </button>
            
            <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-black bg-clip-text text-transparent mb-4">
              🛒 お買い物メモへようこそ！
            </h3>
            
            <div className="space-y-4 text-slate-600">
              <p className="text-lg">このアプリの使い方：</p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="bg-slate-100 text-slate-800 px-2 py-1 rounded text-sm font-semibold">1</span>
                  <p>「新しいタスクを追加」で買い物リストと期日を入力</p>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="bg-slate-100 text-slate-800 px-2 py-1 rounded text-sm font-semibold">2</span>
                  <p>買い物が完了したらチェックボックスをクリック</p>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="bg-slate-100 text-slate-800 px-2 py-1 rounded text-sm font-semibold">3</span>
                  <p>「完了済みを削除」で終わったタスクを整理</p>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <p className="text-amber-800 text-sm">
                  💡 期限が過ぎたタスクは赤く表示されます
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowModal(false)}
              className="w-full mt-6 bg-gradient-to-r from-gray-800 to-black text-white py-3 rounded-xl hover:from-gray-900 hover:to-gray-800 transition-all duration-200 font-semibold"
            >
              買い物を始める！
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulerApp;